<template>
  <div class="card">
    <div class="card-header">{{ $t('createShipment.label.User Account') }}</div>

    <div class="card-body">
      <b-col sm="12">
        <b-form-group
            label-cols-sm="3"
            :label="$t('createShipment.label.Company')"
            label-align-sm="left"
            :name="fieldNames.COMPANY"
            v-if="getCompanies.length > 1"
        >
          <b-input-group size="sm">
            <b-col sm="11">
              <vSelect
                  :dynamicWidth="true"
                  :options="getCompanies"
                  label="name"
                  v-model="company"
                  :reduce="(company) => company.id"
                  :placeholder="$t('createShipment.placeholder.Choose Company')"
                  name="companyDropdown"
                  @input="v.shipment.accountId.$touch()"
                  :clearable="false"
                  class="useraccount-dropdown"
              />
            </b-col>
          </b-input-group>
        </b-form-group>

        <b-form-group
            label-cols-sm="3"
            :label="$t('createShipment.label.Account')"
            label-align-sm="left"
            :name="fieldNames.ACCOUNT"
            v-if="display(fieldNames.ACCOUNT)"
        >
          <b-input-group size="sm">
            <b-col sm="11">
              <vSelect
                  :dynamicWidth="true"
                  :options="getCompanyAccounts"
                  label="displayName"
                  v-model="account"
                  :reduce="(account) => account.id"
                  :placeholder="$t('createShipment.placeholder.Choose Account')"
                  name="accountDropdown"
                  :class="{ 'is-invalid': v.shipment.accountId.$error }"
                  @input="v.shipment.accountId.$touch()"
                  :clearable="false"
                  class="useraccount-dropdown"
              />
              <b-form-invalid-feedback
                  :name="'accountDropdownError'"
                  :state="!v.shipment.accountId.$error"
                  v-if="v.shipment.accountId.$dirty"
              >
                <validationRequired
                    fieldName="createShipment.label.Account"
                    :fieldValid="v.shipment.accountId.required"
                />
              </b-form-invalid-feedback>
            </b-col>
            <b-input-group-append>
              <div>
                <font-awesome-icon
                    icon="exclamation-circle"
                    size="lg"
                />
              </div>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        <b-form-group
            label-cols-sm="3"
            :label="$t('createShipment.label.Import')"
            label-align-sm="left"
            :name="fieldNames.IMPORT"
            v-if="display(fieldNames.IMPORT)"
        >
          <b-input-group size="sm">
            <b-col sm="10">
              <b-form-checkbox
                  class="float-left"
                  size="sm"
                  name="importCheckbox"
                  v-model="isImport"
                  @change="shipmentTypeSelected('Import')"
              ></b-form-checkbox>
            </b-col>
          </b-input-group>
        </b-form-group>
        <b-form-group
            label-cols-sm="3"
            :label="$t('createShipment.label.Cross')"
            label-align-sm="left"
            :name="fieldNames.CROSS"
            v-if="display(fieldNames.CROSS)"
        >
          <b-input-group size="sm">
            <b-col sm="10">
              <b-form-checkbox
                  class="float-left"
                  size="sm"
                  name="crossCheckbox"
                  v-model="isCross"
                  @change="shipmentTypeSelected('Cross')"
              ></b-form-checkbox>
            </b-col>
          </b-input-group>
        </b-form-group>
      </b-col>
    </div>
  </div>
</template>

<script>
import vSelect from './vSelect';
import viewOptions from '../mixins/viewOptionsMixin';
import resetMixin from '../mixins/resetMixin';
import 'vue-select/dist/vue-select.css';
import { EventBus } from '@/services/event-bus.js';
import pickup from '@/components/pickup';
import { FIELD_NAMES } from '../store/nameConstants';
import { ACTIONS, CREATE_SHIPMENT_PREFIX, CORE_PREFIX } from '../store/constants';
import { EVENTS } from '../store/nameConstants';
import validationRequired from './Validation/ValidationRequired';
import requiredIcon from './requiredIcon';
import { mapGetters } from 'vuex';

export default {
  mixins: [viewOptions, resetMixin],

  components: {
    vSelect,
    pickup,
    validationRequired,
    requiredIcon,
  },
  props: {
    shipment: { type: Object, required: true },
    v: { required: true },
  },
  watch: {
    'shipment.accountId': {
      handler() {
        this.$store.dispatch(
            CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.CLEAR_SHOW_SHIPPING_OPTIONS
        );
      },
    },
    isImport: function (val) {
      this.$store.dispatch(
          CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.CLEAR_SHOW_SHIPPING_OPTIONS
      );
    },
    isCross: function (val) {
      this.$store.dispatch(
          CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.CLEAR_SHOW_SHIPPING_OPTIONS
      );
    },
    reset() {
      this.isImport = false;
      this.isCross = false;
      this.shipment.shipmentType = 'Export';
      this.shipment.pickup = null;

      this.resetReceiverPays();

      this.resetColli();
    },
  },
  data() {
    return {
      isImport: false,
      isCross: false,
      accountId: null,
      fieldNames: FIELD_NAMES,
    };
  },
  computed: {
    ...mapGetters('core', ['getCompanies', 'getUserData', 'getCompanyAccounts']),

    account: {
      get() {
        const r = this.getSelectedCompanyAccount();

        return r;
      },
      set(newId) {
        this.shipment.accountId = newId;
        this.$store.dispatch(CORE_PREFIX + ACTIONS.CORE.SET_ACCOUNT, newId);

        this.$store.dispatch(
            CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.FETCH_BOOKING_OPTIONS
        );
        this.$store.dispatch(
            CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.FETCH_DEFAULT_VALUES
        );
        this.$store.dispatch(
            CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.FETCH_COMPANY_ACCOUNT_ADDRESSES
        );
      },
    },
    company: {
      get() {
        return this.getSelectedCompany();
      },
      set(value) {
        this.$store.dispatch(CORE_PREFIX + ACTIONS.CORE.CHANGE_COMPANY, value);

        this.isReset = new Date(); //New date to trigger mixin
      },
    },
  },
  methods: {
    ...mapGetters('createShipment', ['getSelectedCompany', 'getSelectedCompanyAccount']),

    shipmentTypeSelected(type) {
      const currentShipmentType = this.shipment.shipmentType;
      this.shipment.shipmentType = type;

      if (type === 'Import' && currentShipmentType === 'Import') {
        this.shipment.shipmentType = 'Export';
      }

      if (type === 'Cross' && currentShipmentType === 'Cross') {
        this.shipment.shipmentType = 'Export';
      }

      this.isImport = this.shipment.shipmentType === 'Import';
      this.isCross = this.shipment.shipmentType === 'Cross';

      EventBus.$emit(EVENTS.SHIPMENT_TYPE_UPDATE, {
        to: this.shipment.shipmentType,
        from: currentShipmentType,
      });
    },

    resetReceiverPays() {
      this.shipment.receiverPays = null;

      this.$store.dispatch(
          CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.CHANGE_ISRECEIVERPAYS,
          false
      );
    },

    resetColli() {
      this.shipment.collis = [];
      this.shipment.colliDetails.numberOfColli = 1;

      this.shipment.collis.push({
        height: 0,
        length: 0,
        width: 0,
        weight: 0,
        dryIce: 0,
        customPackageType: '',
        packageType: 'Other',
        packagingId: 0,
      });
    },
    setImport() {
      this.shipmentTypeSelected('Import');
    },
    setCross() {
      this.shipmentTypeSelected('Cross');
    },
  },
};
</script>

<style>
.mb-2 {
  margin-bottom: 10px;
}

.useraccount-dropdown div div span {
  width: 180px !important;
  height: 18px !important;
  overflow: hidden;
  white-space: nowrap;
}

.useraccount-dropdown ul li {
  overflow: hidden;
  white-space: nowrap;
}
</style>
